import React, { useMemo, useState } from 'react';
import {
  Activity,
  AlertCircle,
  AlertOctagon,
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronUp,
  Dumbbell,
  Heart,
  Info,
  Shield,
  Star,
  Target,
  User,
  Zap,
  X,
  Calendar,
} from 'lucide-react';
import type { SurveyResponse } from '../types';
import { useSurveyStore } from '../store/useSurveyStore';
import {
  analyzeSurveyResponse,
  buildStarterPlanFromAnalysis,
  type AnalysisResult,
  type Recommendation,
  type ExerciseWarning,
  type ExerciseRecommendation,
} from '../logic/surveyAnalysis';
import { usePlanStore } from '../store/usePlanStore';
import { EXERCISE_DB } from '../data/exercises';

interface Props {
  surveyId: string;
  responseId: string;
  onClose: () => void;
}

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  injury: <AlertOctagon size={14} className="text-red-500" />,
  goal: <Target size={14} className="text-brand-500" />,
  schedule: <Calendar size={14} className="text-blue-500" />,
  equipment: <Dumbbell size={14} className="text-amber-500" />,
  experience: <Star size={14} className="text-purple-500" />,
  nutrition: <Heart size={14} className="text-pink-500" />,
  general: <Info size={14} className="text-slate-500" />,
};

const CATEGORY_LABELS: Record<string, string> = {
  injury: 'Kontuzja',
  goal: 'Cel treningowy',
  schedule: 'Harmonogram',
  equipment: 'Sprzęt',
  experience: 'Doświadczenie',
  nutrition: 'Żywienie',
  general: 'Ogólne',
};

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'border-l-red-500 bg-red-50/50 dark:bg-red-950/20',
  warning: 'border-l-amber-500 bg-amber-50/50 dark:bg-amber-950/20',
  info: 'border-l-blue-400 bg-blue-50/50 dark:bg-blue-950/20',
};

export default function SurveyAnalysis({ surveyId, responseId, onClose }: Props) {
    const createPlan = usePlanStore((s) => s.createPlan);
    const updateSession = usePlanStore((s) => s.updateSession);
    const addExercise = usePlanStore((s) => s.addExercise);
  const addWarmupExercise = usePlanStore((s) => s.addWarmupExercise);
  const updateExercise = usePlanStore((s) => s.updateExercise);
    const [creatingStarterPlan, setCreatingStarterPlan] = useState(false);

  const survey = useSurveyStore((s) => s.getSurveyById(surveyId));
  const responses = useSurveyStore((s) => s.getResponsesBySurvey(surveyId));
  const response = responses.find((r) => r.id === responseId);

  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['profile', 'starter', 'recommendations', 'avoid', 'recommend', 'split', 'volume', 'tips'])
  );

  const toggleSection = (s: string) =>
    setExpandedSections((prev) => {
      const next = new Set(prev);
      next.has(s) ? next.delete(s) : next.add(s);
      return next;
    });

  const analysis: AnalysisResult | null = useMemo(() => {
    if (!survey || !response) return null;
    return analyzeSurveyResponse(survey, response);
  }, [survey, response]);

  const starterDraft = useMemo(() => {
    if (!analysis) return null;
    return buildStarterPlanFromAnalysis(analysis);
  }, [analysis]);

  const handleCreateStarterPlan = async () => {
    if (!starterDraft || creatingStarterPlan) return;

    setCreatingStarterPlan(true);
    try {
      await createPlan({
        name: starterDraft.name,
        clientName: starterDraft.clientName,
        goal: starterDraft.goal,
        trainingLevel: starterDraft.trainingLevel,
        mesocycleLength: starterDraft.mesocycleLength,
        frequency: starterDraft.frequency,
        fatigueTolerance: starterDraft.fatigueTolerance,
        progressionModel: starterDraft.progressionModel,
      });

      const createdPlan = usePlanStore.getState().plan;
      if (!createdPlan) return;

      for (const week of createdPlan.weeks) {
        week.sessions.forEach((session, idx) => {
          const t = starterDraft.sessionTemplates[idx % starterDraft.sessionTemplates.length];
          if (!t) return;
          updateSession(week.id, session.id, {
            day: t.day,
            focus: t.focus,
          });

          t.exerciseIds.forEach((exerciseId) => {
            const def = EXERCISE_DB.find((e) => e.id === exerciseId);
            if (def) {
              addExercise(week.id, session.id, def);
            }
          });

          t.warmupExerciseIds.forEach((warmupId) => {
            const def = EXERCISE_DB.find((e) => e.id === warmupId);
            if (def) {
              addWarmupExercise(week.id, session.id, def);
            }
          });

          const refreshedSession = usePlanStore
            .getState()
            .plan
            ?.weeks
            .find((w) => w.id === week.id)
            ?.sessions
            .find((s) => s.id === session.id);

          if (refreshedSession) {
            refreshedSession.exercises.forEach((ex) => {
              const preset = starterDraft.exercisePresets[ex.exerciseDefId];
              if (!preset) return;
              updateExercise(week.id, session.id, ex.id, {
                sets: preset.sets,
                reps: preset.reps,
                loadType: preset.loadType,
                load: preset.load,
                rest: preset.rest,
              });
            });
          }
        });
      }
    } finally {
      setCreatingStarterPlan(false);
    }
  };

  if (!survey || !response || !analysis) {
    return (
      <div className="text-center py-12">
        <AlertCircle size={32} className="mx-auto text-slate-400 mb-2" />
        <p className="text-sm text-slate-500">Nie znaleziono danych do analizy.</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-100 dark:bg-brand-950 flex items-center justify-center">
            <Activity size={22} className="text-brand-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold">Analiza ankiety: {analysis.clientName}</h2>
            <p className="text-xs text-slate-400">
              Automatyczne rekomendacje na podstawie odpowiedzi klienta
            </p>
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
        >
          <X size={18} />
        </button>
      </div>

      {/* ── Client Profile Card ──────────── */}
      <Section
        id="profile"
        title="Profil klienta"
        icon={<User size={16} className="text-brand-500" />}
        expanded={expandedSections.has('profile')}
        onToggle={() => toggleSection('profile')}
      >
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
          <StatCard label="Wiek" value={analysis.clientAge ? `${analysis.clientAge} lat` : '-'} />
          <StatCard label="Płeć" value={analysis.clientGender || '-'} />
          <StatCard label="Wzrost" value={analysis.clientHeight ? `${analysis.clientHeight} cm` : '-'} />
          <StatCard label="Waga" value={analysis.clientWeight ? `${analysis.clientWeight} kg` : '-'} />
        </div>

        {analysis.bmi && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
            <StatCard label="BMI" value={String(analysis.bmi)} />
            <StatCard label="Kategoria BMI" value={analysis.bmiCategory || '-'} />
            <StatCard
              label="Poziom"
              value={
                analysis.trainingLevel === 'beginner' ? 'Początkujący'
                  : analysis.trainingLevel === 'intermediate' ? 'Średnio'
                    : analysis.trainingLevel === 'advanced' ? 'Zaawansowany'
                      : analysis.trainingLevel === 'elite' ? 'Ekspert'
                        : '-'
              }
            />
            <StatCard label="Czas sesji" value={analysis.sessionDuration ? `${analysis.sessionDuration} min` : '-'} />
          </div>
        )}

        {/* Goals & equipment */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Cele treningowe</p>
            <div className="flex flex-wrap gap-1">
              {analysis.goals.length > 0 ? analysis.goals.map((g, i) => (
                <span key={i} className="px-2 py-0.5 rounded bg-brand-100 dark:bg-brand-950 text-brand-700 dark:text-brand-300 text-xs font-medium">
                  {g}
                </span>
              )) : <span className="text-xs text-slate-400">Brak</span>}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Dostępne dni</p>
            <div className="flex flex-wrap gap-1">
              {analysis.availableDays.length > 0 ? analysis.availableDays.map((d, i) => (
                <span key={i} className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-medium">
                  {d}
                </span>
              )) : <span className="text-xs text-slate-400">Brak danych</span>}
            </div>
          </div>
          <div>
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Sprzęt</p>
            <div className="flex flex-wrap gap-1">
              {analysis.equipment.length > 0 ? analysis.equipment.map((e, i) => (
                <span key={i} className="px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-medium">
                  {e}
                </span>
              )) : <span className="text-xs text-slate-400">Brak danych</span>}
            </div>
          </div>
          {analysis.injuries && (
            <div>
              <p className="text-[10px] font-semibold text-red-400 uppercase tracking-wider mb-1">Kontuzje / Ograniczenia</p>
              <p className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 rounded-lg p-2">
                {analysis.injuries}
              </p>
            </div>
          )}
        </div>

        {analysis.currentActivity && (
          <div className="mt-3">
            <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Obecna aktywność</p>
            <p className="text-xs text-slate-600 dark:text-slate-300">{analysis.currentActivity}</p>
          </div>
        )}
      </Section>

      {/* ── Starter Plan Draft ───────────── */}
      {starterDraft && (
        <Section
          id="starter"
          title="Plan wyjściowy dla trenera"
          icon={<Dumbbell size={16} className="text-green-600" />}
          expanded={expandedSections.has('starter')}
          onToggle={() => toggleSection('starter')}
        >
          <div className="space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <StatCard label="Cel" value={starterDraft.goal} />
              <StatCard label="Poziom" value={starterDraft.trainingLevel} />
              <StatCard label="Sesje/tydz." value={String(starterDraft.frequency)} />
              <StatCard label="Mezocykl" value={`${starterDraft.mesocycleLength} tyg.`} />
            </div>

            <div>
              <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Podział sesji</p>
              <div className="space-y-1.5">
                {starterDraft.sessionTemplates.map((d, i) => (
                  <div key={`${d.day}-${i}`} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                    <span className="text-xs font-bold text-brand-600 dark:text-brand-400 w-24 flex-shrink-0">
                      {d.day}
                    </span>
                    <div className="min-w-0">
                      <div className="text-sm">{d.focus}</div>
                      <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate">
                        {d.exerciseIds
                          .map((id) => EXERCISE_DB.find((e) => e.id === id)?.name)
                          .filter(Boolean)
                          .join(' • ')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <ul className="space-y-1">
              {starterDraft.rationale.map((line, i) => (
                <li key={i} className="text-xs text-slate-500 dark:text-slate-400">• {line}</li>
              ))}
            </ul>

            <div className="pt-1">
              <button
                onClick={handleCreateStarterPlan}
                disabled={creatingStarterPlan}
                className="px-4 py-2 rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-semibold transition"
              >
                {creatingStarterPlan ? 'Tworzenie planu...' : 'Utwórz plan startowy'}
              </button>
            </div>
          </div>
        </Section>
      )}

      {/* ── Recommendations ──────────────── */}
      {analysis.recommendations.length > 0 && (
        <Section
          id="recommendations"
          title={`Rekomendacje (${analysis.recommendations.length})`}
          icon={<Shield size={16} className="text-amber-500" />}
          expanded={expandedSections.has('recommendations')}
          onToggle={() => toggleSection('recommendations')}
          badge={analysis.recommendations.filter((r) => r.severity === 'critical').length > 0
            ? `${analysis.recommendations.filter((r) => r.severity === 'critical').length} krytycznych`
            : undefined}
          badgeColor="red"
        >
          <div className="space-y-2">
            {analysis.recommendations
              .sort((a, b) => severityOrder(a.severity) - severityOrder(b.severity))
              .map((rec, i) => (
                <div
                  key={i}
                  className={`border-l-4 rounded-lg p-3 ${SEVERITY_COLORS[rec.severity]}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {CATEGORY_ICONS[rec.category] || <Info size={14} />}
                    <span className="text-[10px] font-semibold text-slate-400 uppercase">
                      {CATEGORY_LABELS[rec.category] || rec.category}
                    </span>
                    <span className={`text-[9px] px-1 py-0.5 rounded font-bold ${rec.severity === 'critical' ? 'bg-red-200 dark:bg-red-900 text-red-700 dark:text-red-300'
                        : rec.severity === 'warning' ? 'bg-amber-200 dark:bg-amber-900 text-amber-700 dark:text-amber-300'
                          : 'bg-blue-200 dark:bg-blue-900 text-blue-700 dark:text-blue-300'
                      }`}>
                      {rec.severity === 'critical' ? 'WAŻNE' : rec.severity === 'warning' ? 'UWAGA' : 'INFO'}
                    </span>
                  </div>
                  <p className="font-semibold text-sm">{rec.title}</p>
                  <p className="text-xs text-slate-600 dark:text-slate-400 mt-1 leading-relaxed">
                    {rec.description}
                  </p>
                </div>
              ))}
          </div>
        </Section>
      )}

      {/* ── Exercises to AVOID ───────────── */}
      {analysis.exercisesToAvoid.length > 0 && (
        <Section
          id="avoid"
          title={`Ćwiczenia do unikania / ostrożności (${analysis.exercisesToAvoid.length})`}
          icon={<AlertOctagon size={16} className="text-red-500" />}
          expanded={expandedSections.has('avoid')}
          onToggle={() => toggleSection('avoid')}
        >
          <div className="space-y-1">
            {analysis.exercisesToAvoid
              .sort((a, b) => (a.severity === 'avoid' ? -1 : 1) - (b.severity === 'avoid' ? -1 : 1))
              .map((ew, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-3 p-2.5 rounded-lg transition ${ew.severity === 'avoid'
                      ? 'bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800'
                      : ew.severity === 'caution'
                        ? 'bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800'
                        : 'bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700'
                    }`}
                >
                  <span className={`flex-shrink-0 mt-0.5 text-[9px] font-bold px-1.5 py-0.5 rounded ${ew.severity === 'avoid'
                      ? 'bg-red-200 dark:bg-red-900 text-red-700 dark:text-red-300'
                      : 'bg-amber-200 dark:bg-amber-900 text-amber-700 dark:text-amber-300'
                    }`}>
                    {ew.severity === 'avoid' ? 'UNIKAJ' : 'OSTROŻNIE'}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">{ew.exerciseName}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{ew.reason}</p>
                    {ew.alternative && (
                      <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                        Alternatywa: {ew.alternative}
                      </p>
                    )}
                  </div>
                </div>
              ))}
          </div>
        </Section>
      )}


      {/* ── Suggested Split ──────────────── */}
      <Section
        id="split"
        title="Sugerowany podział treningowy"
        icon={<Calendar size={16} className="text-blue-500" />}
        expanded={expandedSections.has('split')}
        onToggle={() => toggleSection('split')}
      >
        <div className="mb-3">
          <h4 className="font-bold text-sm">{analysis.suggestedSplit.name}</h4>
          <p className="text-xs text-slate-500 dark:text-slate-400">{analysis.suggestedSplit.description}</p>
        </div>
        <div className="space-y-1.5">
          {analysis.suggestedSplit.days.map((d, i) => (
            <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
              <span className="text-xs font-bold text-brand-600 dark:text-brand-400 w-16 flex-shrink-0">
                {d.day}
              </span>
              <span className="text-sm">{d.focus}</span>
            </div>
          ))}
        </div>
      </Section>

      {/* ── Volume Guidelines ────────────── */}
      <Section
        id="volume"
        title="Wytyczne objętości (serie/tydzień)"
        icon={<Zap size={16} className="text-purple-500" />}
        expanded={expandedSections.has('volume')}
        onToggle={() => toggleSection('volume')}
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-semibold text-slate-400 uppercase">
                <th className="text-left p-2">Grupa mięśniowa</th>
                <th className="text-center p-2">Serie/tydz.</th>
                <th className="text-left p-2">Uwagi</th>
              </tr>
            </thead>
            <tbody>
              {analysis.volumeGuidelines.map((vg, i) => (
                <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                  <td className="p-2 font-medium">{vg.muscle}</td>
                  <td className="p-2 text-center">
                    <span className="px-2 py-0.5 rounded bg-brand-100 dark:bg-brand-950 text-brand-700 dark:text-brand-300 text-xs font-bold">
                      {vg.setsPerWeek}
                    </span>
                  </td>
                  <td className="p-2 text-xs text-slate-500 dark:text-slate-400">{vg.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* ── General Tips ─────────────────── */}
      {analysis.generalTips.length > 0 && (
        <Section
          id="tips"
          title="Ogólne wskazówki"
          icon={<Info size={16} className="text-slate-500" />}
          expanded={expandedSections.has('tips')}
          onToggle={() => toggleSection('tips')}
        >
          <ul className="space-y-1.5">
            {analysis.generalTips.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-slate-600 dark:text-slate-400 leading-relaxed">
                <span className="flex-shrink-0 mt-0.5 w-4 h-4 rounded-full bg-brand-100 dark:bg-brand-950 text-brand-600 dark:text-brand-400 flex items-center justify-center text-[9px] font-bold">
                  {i + 1}
                </span>
                {tip}
              </li>
            ))}
          </ul>
        </Section>
      )}
    </div>
  );
}

// ── Helpers ─────────────────────────────────

function severityOrder(s: string): number {
  if (s === 'critical') return 0;
  if (s === 'warning') return 1;
  return 2;
}

function StatCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-center">
      <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
      <p className="text-sm font-bold mt-0.5">{value}</p>
    </div>
  );
}

interface SectionProps {
  id: string;
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  badge?: string;
  badgeColor?: string;
  children: React.ReactNode;
}

function Section({ id, title, icon, expanded, onToggle, badge, badgeColor, children }: SectionProps) {
  return (
    <div className="mb-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-2 p-4 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition text-left"
      >
        {icon}
        <span className="font-semibold text-sm flex-1">{title}</span>
        {badge && (
          <span className={`text-[9px] px-1.5 py-0.5 rounded font-bold ${badgeColor === 'red'
              ? 'bg-red-200 dark:bg-red-900 text-red-700 dark:text-red-300'
              : 'bg-brand-100 dark:bg-brand-950 text-brand-600'
            }`}>
            {badge}
          </span>
        )}
        {expanded ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
      </button>
      {expanded && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}
